import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import {
  Search,
  Filter,
  ArrowLeft,
  DollarSign,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  AlertCircle
} from 'lucide-react'

interface LoanRequest {
  id: string
  seller_id: string
  amount: number
  purpose: string
  business_plan: string | null
  status: 'pending' | 'approved' | 'rejected'
  admin_notes: string | null
  created_at: string
  updated_at: string
  approved_amount: number | null
  repayment_terms: string | null
  rejection_reason: string | null
  seller: {
    id: string
    full_name: string | null
    email: string | null
    avatar_url: string | null
  } | null
}

export function AdminLoansPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [loans, setLoans] = useState<LoanRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending')
  const [searchQuery, setSearchQuery] = useState('')
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [selectedLoan, setSelectedLoan] = useState<LoanRequest | null>(null)
  const [showLoanDetails, setShowLoanDetails] = useState(false)
  const [responseForm, setResponseForm] = useState({
    status: 'approved',
    approvedAmount: '',
    repaymentTerms: '',
    rejectionReason: '',
    adminNotes: ''
  })

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    fetchLoans()
  }, [user, navigate, filter, searchQuery])

  const fetchLoans = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('loan_requests')
        .select(`
          *,
          seller:profiles!seller_id (
            id,
            full_name,
            email,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false })

      if (filter !== 'all') {
        query = query.eq('status', filter)
      }

      if (searchQuery) {
        query = query.or(`seller.full_name.ilike.%${searchQuery}%,seller.email.ilike.%${searchQuery}%,purpose.ilike.%${searchQuery}%`)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error details:', error)
        throw error
      }
      
      console.log('Fetched loan requests:', data)
      setLoans(data || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch loan requests'
      console.error('Error fetching loans:', err)
      setToastMessage(errorMessage)
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleResponseSubmit = async () => {
    if (!selectedLoan) return

    try {
      setLoading(true)

      const status = responseForm.status
      const updateData: any = { status }

      if (status === 'approved') {
        const approvedAmount = parseFloat(responseForm.approvedAmount)
        if (isNaN(approvedAmount) || approvedAmount <= 0) {
          throw new Error('Veuillez entrer un montant valide')
        }
        updateData.approved_amount = approvedAmount
        updateData.repayment_terms = responseForm.repaymentTerms
      } else if (status === 'rejected') {
        if (!responseForm.rejectionReason) {
          throw new Error('Veuillez fournir une raison de refus')
        }
        updateData.rejection_reason = responseForm.rejectionReason
      }

      updateData.admin_notes = responseForm.adminNotes
      updateData.updated_at = new Date().toISOString()

      const { error } = await supabase
        .from('loan_requests')
        .update(updateData)
        .eq('id', selectedLoan.id)

      if (error) throw error

      // Create notification for seller
      const { error: notifError } = await supabase
        .from('notifications')
        .insert([{
          user_id: selectedLoan.seller_id,
          title: `Demande de prêt ${status === 'approved' ? 'approuvée' : 'refusée'}`,
          content: status === 'approved' 
            ? `Votre demande de prêt de $${selectedLoan.amount} a été approuvée pour un montant de $${responseForm.approvedAmount}.`
            : `Votre demande de prêt de $${selectedLoan.amount} a été refusée.`,
          type: 'system',
          metadata: {
            loan_id: selectedLoan.id,
            status,
            amount: selectedLoan.amount,
            approved_amount: status === 'approved' ? parseFloat(responseForm.approvedAmount) : null
          }
        }])

      if (notifError) {
        console.error('Error creating notification:', notifError)
      }

      setToastMessage(`Demande de prêt ${status === 'approved' ? 'approuvée' : 'refusée'} avec succès`)
      setToastType('success')
      setShowToast(true)
      setShowLoanDetails(false)
      fetchLoans()
    } catch (err) {
      console.error('Error updating loan request:', err)
      setToastMessage(err instanceof Error ? err.message : 'Erreur lors de la mise à jour de la demande de prêt')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Loan Details Modal */}
      {showLoanDetails && selectedLoan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">
                  Détails de la Demande de Prêt
                </h3>
                <button
                  onClick={() => setShowLoanDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-6 space-y-6">
                {/* Seller Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Vendeur</h4>
                  <div className="mt-2 flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      {selectedLoan.seller?.avatar_url ? (
                        <img
                          src={selectedLoan.seller.avatar_url}
                          alt={selectedLoan.seller.full_name || ''}
                          className="h-10 w-10 rounded-full"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {selectedLoan.seller?.full_name || 'Sans nom'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {selectedLoan.seller?.email}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Loan Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Montant demandé</h4>
                    <div className="mt-2 text-2xl font-bold text-gray-900">
                      {formatAmount(selectedLoan.amount)}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Date de la demande</h4>
                    <div className="mt-2 text-sm text-gray-900">
                      {formatDate(selectedLoan.created_at)}
                    </div>
                  </div>
                </div>

                {/* Loan Purpose */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Objectif du prêt</h4>
                  <div className="mt-2 bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedLoan.purpose}</p>
                  </div>
                </div>

                {/* Business Plan */}
                {selectedLoan.business_plan && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Plan d'affaires</h4>
                    <div className="mt-2 bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedLoan.business_plan}</p>
                    </div>
                  </div>
                )}

                {/* Admin Response Form */}
                {selectedLoan.status === 'pending' ? (
                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h4 className="text-base font-medium text-gray-900 mb-4">Réponse à la demande</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Décision
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <button
                            type="button"
                            onClick={() => setResponseForm(prev => ({ ...prev, status: 'approved' }))}
                            className={`flex items-center justify-center px-4 py-2 border-2 rounded-lg ${
                              responseForm.status === 'approved'
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <CheckCircle className={`h-5 w-5 mr-2 ${
                              responseForm.status === 'approved' ? 'text-green-500' : 'text-gray-400'
                            }`} />
                            Approuver
                          </button>
                          <button
                            type="button"
                            onClick={() => setResponseForm(prev => ({ ...prev, status: 'rejected' }))}
                            className={`flex items-center justify-center px-4 py-2 border-2 rounded-lg ${
                              responseForm.status === 'rejected'
                                ? 'border-red-500 bg-red-50 text-red-700'
                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <XCircle className={`h-5 w-5 mr-2 ${
                              responseForm.status === 'rejected' ? 'text-red-500' : 'text-gray-400'
                            }`} />
                            Refuser
                          </button>
                        </div>
                      </div>

                      {responseForm.status === 'approved' && (
                        <>
                          <div>
                            <label htmlFor="approvedAmount" className="block text-sm font-medium text-gray-700">
                              Montant approuvé *
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <DollarSign className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="number"
                                id="approvedAmount"
                                required
                                min="100"
                                step="0.01"
                                value={responseForm.approvedAmount}
                                onChange={(e) => setResponseForm(prev => ({ ...prev, approvedAmount: e.target.value }))}
                                className="block w-full pl-10 pr-12 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm py-2"
                                placeholder={selectedLoan.amount.toString()}
                              />
                            </div>
                          </div>

                          <div>
                            <label htmlFor="repaymentTerms" className="block text-sm font-medium text-gray-700">
                              Conditions de remboursement *
                            </label>
                            <textarea
                              id="repaymentTerms"
                              required
                              rows={3}
                              value={responseForm.repaymentTerms}
                              onChange={(e) => setResponseForm(prev => ({ ...prev, repaymentTerms: e.target.value }))}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm px-3 py-2"
                              placeholder="Décrivez les conditions de remboursement (durée, taux, etc.)"
                            />
                          </div>
                        </>
                      )}

                      {responseForm.status === 'rejected' && (
                        <div>
                          <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700">
                            Raison du refus *
                          </label>
                          <textarea
                            id="rejectionReason"
                            required
                            rows={3}
                            value={responseForm.rejectionReason}
                            onChange={(e) => setResponseForm(prev => ({ ...prev, rejectionReason: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm px-3 py-2"
                            placeholder="Expliquez pourquoi la demande est refusée"
                          />
                        </div>
                      )}

                      <div>
                        <label htmlFor="adminNotes" className="block text-sm font-medium text-gray-700">
                          Notes administratives (optionnel)
                        </label>
                        <textarea
                          id="adminNotes"
                          rows={3}
                          value={responseForm.adminNotes}
                          onChange={(e) => setResponseForm(prev => ({ ...prev, adminNotes: e.target.value }))}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm px-3 py-2"
                          placeholder="Notes internes ou informations supplémentaires pour le vendeur"
                        />
                      </div>

                      <div className="flex justify-end space-x-3 pt-4">
                        <Button
                          variant="secondary"
                          onClick={() => setShowLoanDetails(false)}
                        >
                          Annuler
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleResponseSubmit}
                          disabled={loading || (
                            responseForm.status === 'approved' && (!responseForm.approvedAmount || !responseForm.repaymentTerms)
                          ) || (
                            responseForm.status === 'rejected' && !responseForm.rejectionReason
                          )}
                        >
                          {loading ? 'Traitement...' : 'Soumettre la réponse'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`p-4 rounded-lg border ${
                    selectedLoan.status === 'approved' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center">
                      {selectedLoan.status === 'approved' ? (
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500 mr-2" />
                      )}
                      <h4 className={`text-sm font-medium ${
                        selectedLoan.status === 'approved' ? 'text-green-800' : 'text-red-800'
                      }`}>
                        Demande {selectedLoan.status === 'approved' ? 'approuvée' : 'refusée'}
                      </h4>
                    </div>

                    {selectedLoan.status === 'approved' && (
                      <div className="mt-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-green-800">Montant approuvé:</span>
                          <span className="text-sm font-medium text-green-800">
                            {formatAmount(selectedLoan.approved_amount || 0)}
                          </span>
                        </div>
                        {selectedLoan.repayment_terms && (
                          <div>
                            <span className="text-sm text-green-800">Conditions de remboursement:</span>
                            <p className="mt-1 text-sm text-green-700">{selectedLoan.repayment_terms}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedLoan.status === 'rejected' && selectedLoan.rejection_reason && (
                      <div className="mt-2">
                        <span className="text-sm text-red-800">Raison du refus:</span>
                        <p className="mt-1 text-sm text-red-700">{selectedLoan.rejection_reason}</p>
                      </div>
                    )}

                    {selectedLoan.admin_notes && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <span className={`text-sm ${
                          selectedLoan.status === 'approved' ? 'text-green-800' : 'text-red-800'
                        }`}>
                          Notes:
                        </span>
                        <p className={`mt-1 text-sm ${
                          selectedLoan.status === 'approved' ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {selectedLoan.admin_notes}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="mr-4"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gestion des Demandes de Prêt
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Examinez et répondez aux demandes de prêt des vendeurs
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un vendeur..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as typeof filter)}
                  className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="approved">Approuvées</option>
                  <option value="rejected">Refusées</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : loans.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune demande de prêt</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucune demande de prêt ne correspond à vos critères
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendeur
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Montant
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Objectif
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loans.map((loan) => (
                      <tr key={loan.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              {loan.seller?.avatar_url ? (
                                <img
                                  src={loan.seller.avatar_url}
                                  alt={loan.seller.full_name || ''}
                                  className="h-10 w-10 rounded-full"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <User className="h-5 w-5 text-gray-500" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {loan.seller?.full_name || 'Sans nom'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {loan.seller?.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm font-medium text-gray-900">
                            <DollarSign className="h-5 w-5 text-gray-400 mr-1" />
                            {loan.amount.toFixed(2)}
                          </div>
                          {loan.status === 'approved' && loan.approved_amount && (
                            <div className="text-xs text-green-600">
                              Approuvé: ${loan.approved_amount.toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 line-clamp-2">
                            {loan.purpose}
                          </div>
                          {loan.business_plan && (
                            <div className="mt-1 flex items-center text-xs text-gray-500">
                              <FileText className="h-3 w-3 mr-1" />
                              Plan d'affaires disponible
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(loan.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            loan.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : loan.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {loan.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                            {loan.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                            {loan.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                            {loan.status === 'pending' ? 'En attente' :
                             loan.status === 'approved' ? 'Approuvée' : 'Refusée'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedLoan(loan)
                              setShowLoanDetails(true)
                              if (loan.status === 'pending') {
                                setResponseForm({
                                  status: 'approved',
                                  approvedAmount: loan.amount.toString(),
                                  repaymentTerms: '',
                                  rejectionReason: '',
                                  adminNotes: ''
                                })
                              }
                            }}
                          >
                            {loan.status === 'pending' ? 'Répondre' : 'Détails'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}